const img = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=${w}`;

export const IMAGES = {
  hero: img("1561501900-3701fa6a0864", 1600),
  poolLounge: img("1549294413-26f195200c16"),
  tropicalStay: img("1520250497591-112f2f40a3f4"),
  boutiquePool: img("1776763019060-fa0663574ae6"),
  archPool: img("1680609989998-6183fcea718b"),
  coastal: img("1533105079780-92b9be482077"),
  beach: img("1507525428034-b723cf961d3e"),
  boardwalk: img("1519046904884-53103b34b206"),
  paris: img("1502602898657-3e91760cbb34"),
  riviera: img("1516483638261-f4dbaf036963"),
  resortPalms: img("1445019980597-93fa8acb246c"),
  resortAerial: img("1566073771259-6a8506099945"),
  hotelRoom: img("1582719478250-c89cae4dc85b"),
  resortPool: img("1571896349842-33c89424de2d"),
  roadTrip: img("1469854523086-cc02fe5d8800"),
  canoeLake: img("1476514525535-07fb3b4ae5f1"),
  hotelExterior: img("1551882547-ff40c63fe5fa"),
  planning: img("1488646953014-85cb44e25828"),
  apartment: img("1522708323590-d24dbb6b0267"),
};

const booking = (q) =>
  `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(q)}`;

export const LINKS = {
  bookingCharleston: booking("Charleston, South Carolina, United States"),
  bookingOrlando: booking("Orlando, Florida, United States"),
  bookingSavannah: booking("Savannah, Georgia, United States"),
  bookingTampa: booking("Tampa, Florida, United States"),
  bookingBanff: booking("Banff, Alberta, Canada"),
  bookingParis: booking("Paris, Ile-de-France, France"),
  viator: "https://www.viator.com/",
  luggage: "https://monos.com",
  esim: "https://www.airalo.com",
  insurance: "https://www.insuremytrip.com",
  planner: "https://wanderlog.com",
};

export const MARQUEE_ITEMS = [
  "Destination Guides",
  "Honest Hotel Comparisons",
  "Three-Day Itineraries",
  "Carry-On Packing Systems",
  "eSIM & Connectivity",
  "Travel Insurance, Explained",
  "Neighborhood Guides",
  "Seasonal Roundups",
  "Realistic Travel Budgets",
  "Curated Experiences",
];

export const CHAPTERS = [
  {
    chapter: "01",
    title: "Curation Over Aggregation",
    body: "We filter out hundreds of mediocre stays to spotlight only properties with distinct character, exceptional service, and true value.",
  },
  {
    chapter: "02",
    title: "Practical Clarity",
    body: "No fluff or exaggerated reviews. Every guide provides actionable itineraries, optimal seasons, and realistic budgeting insights.",
  },
  {
    chapter: "03",
    title: "Transparent Recommendations",
    body: "Our reviews are completely independent. Affiliate links support our research without dictating our rankings.",
  },
];

export const DESTINATIONS = [
  {
    slug: "charleston",
    city: "Charleston",
    region: "South Carolina",
    title: "A First-Time Visitor's Guide to Charleston",
    blurb:
      "Where to stay, eat, and explore in the Lowcountry — from the Historic District to Sullivan's Island, with the bookings to make ahead.",
    image: IMAGES.boardwalk,
    booking: LINKS.bookingCharleston,
    bestFor: "Couples & food travelers",
    days: "3–4 days",
  },
  {
    slug: "orlando",
    city: "Orlando",
    region: "Florida",
    title: "A Three-Day Orlando Itinerary Beyond the Theme Parks",
    blurb:
      "Lake Eola, Winter Park, and the neighborhoods worth your time — plus where to stay when the parks aren't the point.",
    image: IMAGES.resortPool,
    booking: LINKS.bookingOrlando,
    bestFor: "Families & repeat visitors",
    days: "3 days",
  },
  {
    slug: "savannah",
    city: "Savannah",
    region: "Georgia",
    title: "How to Plan a Relaxed Long Weekend in Savannah",
    blurb:
      "Squares, oak-lined streets, and slow mornings. A pacing-first itinerary with stays near Forsyth Park and the riverfront.",
    image: IMAGES.coastal,
    booking: LINKS.bookingSavannah,
    bestFor: "Slow travelers",
    days: "2–3 days",
  },
  {
    slug: "tampa-bay",
    city: "Tampa Bay",
    region: "Florida",
    title: "The Best Weekend Getaways from Tampa Bay",
    blurb:
      "Gulf beaches, riverwalk evenings, and easy coastal drives — the short trips that feel much longer than two days.",
    image: IMAGES.beach,
    booking: LINKS.bookingTampa,
    bestFor: "Weekend escapes",
    days: "2 days",
  },
  {
    slug: "southeast-road-trip",
    city: "The Southeast",
    region: "United States",
    title: "Where to Go for a Long Weekend in the Southeast",
    blurb:
      "A route-based guide linking mountain towns, coastal cities, and the stays that make the drive worthwhile.",
    image: IMAGES.roadTrip,
    booking: LINKS.bookingSavannah,
    bestFor: "Road trippers",
    days: "3–5 days",
  },
  {
    slug: "mountain-lakes",
    city: "Mountain Lakes",
    region: "North America",
    title: "Turquoise Water Escapes Worth the Flight",
    blurb:
      "Alpine lakes, canoe mornings, and lodges with real character — a planner for travelers who measure trips in sunrises.",
    image: IMAGES.canoeLake,
    booking: LINKS.bookingBanff,
    bestFor: "Outdoors-first travelers",
    days: "4–5 days",
  },
];

export const STAYS = [
  {
    slug: "historic-inns",
    category: "Historic Boutique Inns",
    title: "Character-first stays in the Historic District",
    location: "Charleston, SC",
    price: "from $189/night",
    image: IMAGES.hotelRoom,
    why: "Walkable to King Street, porches for slow mornings, and service that remembers your name.",
    booking: LINKS.bookingCharleston,
  },
  {
    slug: "resort-pools",
    category: "Resort Pools & Spas",
    title: "Pool-forward resorts beyond the park gates",
    location: "Orlando, FL",
    price: "from $214/night",
    image: IMAGES.resortAerial,
    why: "Lazy rivers, proper spas, and shuttles that actually run on time — vetted for families and couples alike.",
    booking: LINKS.bookingOrlando,
  },
  {
    slug: "design-hotels",
    category: "Design-Forward Hotels",
    title: "Architectural stays for travelers who notice details",
    location: "Savannah, GA",
    price: "from $176/night",
    image: IMAGES.archPool,
    why: "Considered interiors, quiet courtyards, and locations that put the squares at your doorstep.",
    booking: LINKS.bookingSavannah,
  },
  {
    slug: "apartment-stays",
    category: "Apartment-Style Stays",
    title: "Space to unpack, kitchens that get used",
    location: "Tampa Bay, FL",
    price: "from $142/night",
    image: IMAGES.apartment,
    why: "For longer weekends and traveling families — laundry, living rooms, and neighborhood mornings.",
    booking: LINKS.bookingTampa,
  },
];

export const TOOLS = [
  {
    icon: "Luggage",
    title: "Carry-On Luggage",
    blurb:
      "The features that matter for frequent travelers: shell durability, wheel quality, and honest capacity.",
    cta: "View current luggage pricing",
    url: LINKS.luggage,
    testid: "tool-luggage",
  },
  {
    icon: "Signal",
    title: "eSIM & Connectivity",
    blurb:
      "Do you need an eSIM for international travel? Setup, coverage considerations, and who actually benefits.",
    cta: "Compare eSIM plans",
    url: LINKS.esim,
    testid: "tool-esim",
  },
  {
    icon: "ShieldCheck",
    title: "Travel Insurance",
    blurb:
      "What policies can cover, what they exclude, and when a plan may genuinely be worth considering.",
    cta: "Compare travel-insurance plans",
    url: LINKS.insurance,
    testid: "tool-insurance",
  },
  {
    icon: "Map",
    title: "Itinerary Planning",
    blurb:
      "Tools for itinerary management and collaborative planning that survive contact with a real trip.",
    cta: "Explore trip-planning tools",
    url: LINKS.planner,
    testid: "tool-planner",
  },
];

export const TIMELINE = [
  {
    day: "90",
    title: "Book the framework",
    items: ["Flights and long-haul transport", "Anchor accommodations", "High-demand tours and tickets"],
  },
  {
    day: "60",
    title: "Shape the days",
    items: ["Neighborhood-level stays", "Restaurant reservations that matter", "Rail, transfers, and rentals"],
  },
  {
    day: "30",
    title: "Remove the friction",
    items: ["eSIM and connectivity setup", "Travel insurance review", "Packing system and documents"],
  },
];

export const ARTICLES = [
  {
    index: "01",
    category: "Destination Guide",
    title: "A First-Time Visitor's Guide to Charleston: Where to Stay, Eat, and Explore",
    time: "12 min read",
  },
  {
    index: "02",
    category: "Hotel Comparison",
    title: "Drury Plaza vs. Hyatt Place: Which Is Better for Your Orlando Stay?",
    time: "8 min read",
  },
  {
    index: "03",
    category: "Travel Planning",
    title: "How to Build a Realistic Travel Budget Without Removing the Fun",
    time: "9 min read",
  },
  {
    index: "04",
    category: "Travel Tools",
    title: "The Travel Accessories That Actually Make Airport Days Easier",
    time: "7 min read",
  },
  {
    index: "05",
    category: "Curated Collection",
    title: "10 Thoughtful Hotels for a Florida Anniversary Weekend",
    time: "11 min read",
  },
];
